import type jsPDF from "jspdf";
import type { InvoiceBlockId, InvoiceRenderedBlock } from "@shared/invoice-layout";

/**
 * Observe the same jsPDF calls used for output. Coordinates, font metrics,
 * wrapping and page numbers therefore come from the renderer, not the sketch.
 * Only active cover blocks are recorded; table attachments are not cover blocks.
 */
export function observeInvoiceBounds(pdf: jsPDF) {
  const blocks: InvoiceRenderedBlock[] = [];
  let active: InvoiceBlockId | undefined;
  const record = (x: number, y: number, width: number, height: number) => {
    if (!active || width <= 0 || height <= 0) return;
    blocks.push({ id: active, page: pdf.getCurrentPageInfo().pageNumber, x, y, width, height });
  };
  const text = pdf.text.bind(pdf);
  pdf.text = ((...args: Parameters<jsPDF["text"]>) => {
    const [value, x, y, options] = args;
    if (active && typeof x === "number" && typeof y === "number") {
      const lines = Array.isArray(value) ? value : String(value).split("\n");
      const height = pdf.getFontSize() / pdf.internal.scaleFactor;
      const lineHeight = height * (options?.lineHeightFactor ?? pdf.getLineHeightFactor());
      lines.forEach((line, index) => {
        if (typeof line !== "string" || !line.trim()) return;
        const width = pdf.getTextWidth(line);
        const left = options?.align === "right" ? x - width : options?.align === "center" ? x - width / 2 : x;
        // jsPDF's default baseline is alphabetic. Include ascenders/descenders.
        record(left, y + index * lineHeight - height * 0.8, width, height);
      });
    }
    return text(...args);
  }) as jsPDF["text"];
  const rect = pdf.rect.bind(pdf);
  pdf.rect = ((...args: Parameters<jsPDF["rect"]>) => {
    record(args[0], args[1], args[2], args[3]);
    return rect(...args);
  }) as jsPDF["rect"];
  const roundedRect = pdf.roundedRect.bind(pdf);
  pdf.roundedRect = ((...args: Parameters<jsPDF["roundedRect"]>) => {
    record(args[0], args[1], args[2], args[3]);
    return roundedRect(...args);
  }) as jsPDF["roundedRect"];
  return {
    blocks,
    activate(id?: InvoiceBlockId) { active = id; },
  };
}
